// Collects the technical context a student cannot reliably describe.
//
// Two ring buffers record recent JavaScript errors and recent failed Portal
// requests so a report can say "a save returned 500 on this path" instead of
// "it did not work". Buffers are bounded, same-origin only, and never include
// request or response bodies. URL fields omit origins, queries and fragments;
// error messages are bounded technical text, not a general activity log.

const MAX_EVENTS = 10
const MAX_MESSAGE = 300
const MAX_PATH = 255

const jsErrors = []
const failedRequests = []
const sessionStartedAt = new Date().toISOString()
let installed = false

const push = (buffer, event) => {
  buffer.push(event)
  if (buffer.length > MAX_EVENTS) buffer.shift()
}

const clamp = (value, limit) => (typeof value === 'string' ? value.slice(0, limit) : undefined)

// Paths only. A query string can carry tokens or identifying values and is
// never useful for reproducing a Portal fault.
const samePathOnly = input => {
  if (!input || (typeof input !== 'string' && !(input instanceof URL))) return null
  try {
    const url = new URL(String(input), window.location.origin)
    if (url.origin !== window.location.origin) return null
    return clamp(url.pathname, MAX_PATH)
  } catch {
    return null
  }
}

const onPortalPage = () => /^\/portal(?:\/|$)/.test(window.location.pathname)

const recordError = (message, source, line) => {
  if (!onPortalPage()) return
  const text = clamp(String(message || '').trim(), MAX_MESSAGE)
  if (!text) return

  push(jsErrors, {
    message: text,
    source: samePathOnly(source) || undefined,
    line: Number.isFinite(line) ? Math.min(Math.trunc(line), 10_000_000) : undefined,
    at: new Date().toISOString(),
  })
}

const recordFailedRequest = (method, input, status) => {
  if (!onPortalPage()) return
  const path = samePathOnly(input)
  if (!path || !/^\/portal(?:\/|$)/.test(path) || !Number.isFinite(status)) return

  push(failedRequests, {
    method: String(method || 'GET').toUpperCase().slice(0, 7),
    path,
    status: Math.trunc(status),
    at: new Date().toISOString(),
  })
}

export const installPortalDiagnostics = () => {
  if (installed) return
  installed = true

  window.addEventListener('error', event => {
    if (event.error || event.message) recordError(event.message, event.filename, event.lineno)
  })

  window.addEventListener('unhandledrejection', event => {
    const reason = event.reason
    recordError(reason?.message || reason, reason?.fileName, reason?.lineNumber)
  })

  const originalFetch = window.fetch
  if (typeof originalFetch !== 'function') return

  window.fetch = async function patchedFetch(input, init) {
    const method = init?.method || (typeof input === 'object' ? input?.method : 'GET')
    const target = input instanceof URL ? input.href : (typeof input === 'object' && input !== null ? input.url : input)

    try {
      const response = await originalFetch.call(this, input, init)
      if (!response.ok) recordFailedRequest(method, target, response.status)
      return response
    } catch (error) {
      // A network-level failure has no status. Zero marks "never reached the
      // server", which distinguishes offline/CORS problems from a 500.
      recordFailedRequest(method, target, 0)
      throw error
    }
  }
}

const displayMode = () => {
  if (window.navigator?.standalone === true) return 'standalone'
  const modes = ['standalone', 'minimal-ui', 'fullscreen', 'browser']
  return modes.find(mode => window.matchMedia?.(`(display-mode: ${mode})`).matches) || undefined
}

const connection = () => {
  const source = navigator.connection || navigator.mozConnection || navigator.webkitConnection
  if (!source) return undefined

  return {
    effectiveType: source.effectiveType,
    downlink: typeof source.downlink === 'number' ? source.downlink : undefined,
    saveData: typeof source.saveData === 'boolean' ? source.saveData : undefined,
  }
}

const assetSignature = () => {
  const asset = document.querySelector('script[src*="/vite"], script[src*="/assets"]')
  return clamp(samePathOnly(asset?.getAttribute('src')), 200) || undefined
}

// Snapshotted at submit time so the report describes the session as it was when
// the problem was visible, not a later state.
export const collectPortalDiagnostics = () => ({
  viewport: { width: window.innerWidth, height: window.innerHeight },
  screen: { width: window.screen?.width, height: window.screen?.height },
  devicePixelRatio: window.devicePixelRatio,
  displayMode: displayMode(),
  serviceWorkerControlled: Boolean(navigator.serviceWorker?.controller),
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  locale: navigator.language,
  online: navigator.onLine,
  connection: connection(),
  platform: navigator.platform,
  touchPoints: navigator.maxTouchPoints,
  memoryGb: navigator.deviceMemory,
  assetSignature: assetSignature(),
  sessionStartedAt,
  jsErrors: jsErrors.slice(-MAX_EVENTS),
  failedRequests: failedRequests.slice(-MAX_EVENTS),
})
